import {Button} from '@/components/ui/button/Button'
import {DialogFooter, DialogDismiss} from '@/components/ui/dialog-atoms/DialogAtoms'
import {useTranslation} from '@/translations/i18n'
import {CreateAbsenceFormValidationSchema} from '../../formSchemas'
import {useFormContext} from 'react-hook-form'
import {useCreateAccess} from '../../queries/useCreateAccess'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {absenceFormValuesToPayload} from '../../utils'
import {useDialogContext} from '@ariakit/react'
import {useQueryClient} from '@tanstack/react-query'
import {QUERY_KEYS} from '@/queryClient'
import {useUpdateAccess} from '../../queries/useUpdateAccess'
import {useMemo} from 'react'
import {toast} from '@/utilities/toast/toast'

export const AbsenceDialogFooter = ({accessId}: {accessId?: string}) => {
    const dialog = useDialogContext()
    if (!dialog) {
        throw new Error('Dialog not found')
    }
    const {t} = useTranslation()
    const {
        handleSubmit,
        formState: {isValid}
    } = useFormContext<CreateAbsenceFormValidationSchema>()

    const {mutate: createAccess, isPending: isCreating} = useCreateAccess({
        onSuccess: async () => {
            await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.ACCESSES]})
            await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.ACCESSES_SIGNATURE_SHEETS]})
            toast.success(t('accesses:absence_created_success'))
            dialog.hide()
        }
    })
    const {mutate: updateAccess, isPending: isUpdating} = useUpdateAccess(
        {accessId},
        {
            onSuccess: async () => {
                await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.ACCESSES]})
                await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.ACCESSES_SIGNATURE_SHEETS]})
                toast.success(t('accesses:absence_updated_success'))
                dialog.hide()
            }
        }
    )
    const queryClient = useQueryClient()

    const onSubmit = (data: CreateAbsenceFormValidationSchema) => {
        const payload = absenceFormValuesToPayload(data)
        if (accessId) {
            updateAccess(payload[0])
        } else {
            createAccess(payload)
        }
    }
    const isPending = useMemo(() => isCreating || isUpdating, [isCreating, isUpdating])

    return (
        <DialogFooter>
            <DialogDismiss render={<Button variant="secondary">{t('commons:cancel')}</Button>} />
            <Button variant="primaryNeutral" onClick={handleSubmit(onSubmit)} disabled={!isValid || isPending}>
                {t('commons:confirm')}
                {isPending && <Spinner />}
            </Button>
        </DialogFooter>
    )
}
